import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Wrench, Lightbulb, Heart, AlertTriangle, Flag, MoreVertical, CheckCircle2, Lock } from 'lucide-react';
import { AttachmentImage } from '@/components/support/AttachmentImage';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { TicketStatusBadge } from '@/components/support/TicketStatusBadge';
import { TicketReplyInput } from '@/components/support/TicketReplyInput';
import { TicketUserInfoCard } from '@/components/support/TicketUserInfoCard';
import { useTicketDetails } from '@/hooks/useTicketDetails';
import { useUpdateTicketStatus } from '@/hooks/useUpdateTicketStatus';
import { useUserRole } from '@/hooks/useUserRole';
import { useAuth } from '@/hooks/useAuth';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const typeConfig = {
  request: { icon: Wrench, label: 'Solicitação', color: 'text-blue-500' },
  suggestion: { icon: Lightbulb, label: 'Sugestão', color: 'text-purple-500' },
  compliment: { icon: Heart, label: 'Elogio', color: 'text-green-500' },
  complaint: { icon: AlertTriangle, label: 'Reclamação', color: 'text-red-500' },
  report: { icon: Flag, label: 'Denúncia', color: 'text-red-600' },
};

const SupportTicketDetails = () => {
  const { ticketId } = useParams<{ ticketId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { canAccessAdminPanel } = useUserRole();

  const { ticket, messages, isLoading } = useTicketDetails(ticketId!);
  const updateStatus = useUpdateTicketStatus();

  const isStaff = canAccessAdminPanel;
  const isOwner = ticket?.user_id === user?.id;
  const canReply = (isOwner || isStaff) && ticket?.status !== 'resolved';

  const handleResolve = () => {
    if (ticket && ticketId) {
      updateStatus.mutate({ ticketId, status: 'resolved' });
    }
  };

  const handleReopen = () => {
    if (ticket && ticketId) {
      updateStatus.mutate({ ticketId, status: 'open' });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-black to-gray-900">
        <header className="sticky top-0 z-10 bg-black/95 backdrop-blur-xl border-b border-white/10">
          <div className="flex items-center gap-3 px-4 py-3 max-w-4xl mx-auto">
            <Skeleton className="w-10 h-10 bg-white/10" />
            <Skeleton className="h-6 w-32 flex-1 bg-white/10" />
          </div>
        </header>
        <main className="max-w-4xl mx-auto p-4">
          <Skeleton className="h-48 w-full mb-4 bg-white/10" />
          <Skeleton className="h-32 w-full bg-white/10" />
        </main>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-black to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-white/60 mb-4">Ticket não encontrado</p>
          <Button onClick={() => navigate('/main/support')} className="bg-luxury-red hover:bg-luxury-red/90">
            Voltar para Suporte
          </Button>
        </div>
      </div>
    );
  }

  const typeInfo = typeConfig[ticket.type];
  const TypeIcon = typeInfo.icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-black to-gray-900 pb-8">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-black/95 backdrop-blur-xl border-b border-white/10">
        <div className="flex items-center gap-3 px-4 py-3 max-w-4xl mx-auto">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/main/support')}
            className="hover:bg-white/10 text-white"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-xs text-white/40 font-mono">
                {ticket.ticket_number}
              </span>
              <TicketStatusBadge status={ticket.status} />
            </div>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="hover:bg-white/10 text-white">
                <MoreVertical className="w-5 h-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-black/95 backdrop-blur-xl border-white/10">
              {ticket.status !== 'resolved' && (
                <DropdownMenuItem onClick={handleResolve} className="text-white/80 hover:text-white hover:bg-white/10 focus:bg-white/10 focus:text-white cursor-pointer">
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Marcar como Resolvido
                </DropdownMenuItem>
              )}
              {ticket.status === 'resolved' && (
                <DropdownMenuItem onClick={handleReopen} className="text-white/80 hover:text-white hover:bg-white/10 focus:bg-white/10 focus:text-white cursor-pointer">
                  Reabrir Ticket
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4 space-y-6 animate-fade-in">
        {/* User Info Card - Staff Only */}
        {isStaff && ticket.user_id && (
          <TicketUserInfoCard userId={ticket.user_id} isStaff={isStaff} />
        )}

        {/* Ticket Info Card */}
        <Card className="bg-black/40 border-white/10">
          <CardHeader className="pb-3">
            <div className="flex items-start gap-3">
              <div className={`w-12 h-12 rounded-full bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0 ${typeInfo.color}`}>
                <TypeIcon className="w-6 h-6" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-sm font-medium ${typeInfo.color}`}>
                    {typeInfo.label}
                  </span>
                </div>
                <h1 className="text-lg md:text-xl font-bold text-white">
                  {ticket.subject}
                </h1>
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm text-white/70 whitespace-pre-wrap">
                {ticket.description}
              </p>
            </div>

            {ticket.attachments && ticket.attachments.length > 0 && (
              <div>
                <p className="text-sm font-medium mb-2 text-white">Anexos:</p>
                <div className="grid grid-cols-3 gap-2">
                  {ticket.attachments.map((attachment, index) => (
                    <div 
                      key={index}
                      className="aspect-square rounded-lg overflow-hidden bg-white/5 border border-white/10 hover:border-white/30 transition-colors"
                    >
                      <AttachmentImage 
                        filePath={attachment as string}
                        alt={`Anexo ${index + 1}`}
                        className="w-full h-full object-cover"
                        containerClassName="w-full h-full bg-white/5"
                        asLink
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-white/40 pt-2 border-t border-white/10">
              <span>
                Criado em {format(new Date(ticket.created_at), "dd 'de' MMMM 'às' HH:mm", { locale: ptBR })}
              </span>
              {ticket.resolved_at && (
                <span>
                  Resolvido em {format(new Date(ticket.resolved_at), "dd 'de' MMMM 'às' HH:mm", { locale: ptBR })}
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Messages Timeline */}
        {messages && messages.length > 0 && (
          <Card className="bg-black/40 border-white/10">
            <CardHeader>
              <h2 className="font-semibold text-white">Histórico de Interações</h2>
            </CardHeader>
            <CardContent className="space-y-4">
              {messages.map((message) => {
                const isMessageFromOwner = message.user_id === ticket.user_id;
                const showInternalNote = message.is_internal_note && isStaff;
                
                // Hide internal notes from non-staff
                if (message.is_internal_note && !isStaff) return null;
                
                return (
                  <div 
                    key={message.id} 
                    className={`flex gap-3 ${message.is_internal_note ? 'bg-amber-500/10 -mx-4 px-4 py-3 rounded-lg border border-amber-500/20' : ''}`}
                  >
                    <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center ${
                      isMessageFromOwner 
                        ? 'bg-white/5 border border-white/10' 
                        : 'bg-luxury-pinkBright/20 border border-luxury-pinkBright/30'
                    }`}>
                      <span className={`text-xs font-medium ${isMessageFromOwner ? 'text-white/80' : 'text-luxury-pinkBright'}`}>
                        {isMessageFromOwner ? 'EU' : 'EQ'}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className={`font-medium text-sm ${isMessageFromOwner ? 'text-white' : 'text-luxury-pinkBright'}`}>
                          {message.profiles?.username || 'Usuário'}
                        </span>
                        {!isMessageFromOwner && (
                          <span className="text-[10px] bg-luxury-pinkBright/20 text-luxury-pinkBright px-1.5 py-0.5 rounded">
                            Equipe
                          </span>
                        )}
                        {showInternalNote && (
                          <span className="text-[10px] bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded flex items-center gap-1">
                            <Lock className="w-3 h-3" />
                            Nota Interna
                          </span>
                        )}
                        <span className="text-xs text-white/40">
                          {format(new Date(message.created_at), 'dd/MM HH:mm')}
                        </span>
                      </div>
                      <p className="text-sm text-white/70 whitespace-pre-wrap">
                        {message.content}
                      </p>
                      {/* Message Attachments */}
                      {message.attachments && message.attachments.length > 0 && (
                        <div className="grid grid-cols-3 gap-2 mt-2">
                          {message.attachments.map((attachment, index) => (
                            <div 
                              key={index}
                              className="aspect-square rounded-lg overflow-hidden bg-white/5 border border-white/10 hover:border-white/30 transition-colors"
                            >
                              <AttachmentImage 
                                filePath={attachment as string}
                                alt={`Anexo ${index + 1}`}
                                className="w-full h-full object-cover"
                                containerClassName="w-full h-full bg-white/5"
                                asLink
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        {/* Reply Input */}
        {canReply && ticketId && (
          <TicketReplyInput ticketId={ticketId} isStaff={isStaff} />
        )}

        {/* Status Info */}
        {ticket.status === 'open' && !canReply && (
          <Card className="bg-blue-500/10 border-blue-500/30">
            <CardContent className="pt-6">
              <p className="text-sm text-center text-blue-300">
                Seu ticket está aberto e será analisado pela nossa equipe em breve.
              </p>
            </CardContent>
          </Card>
        )}

        {ticket.status === 'resolved' && (
          <Card className="bg-green-500/10 border-green-500/30">
            <CardContent className="pt-6">
              <p className="text-sm text-center text-green-300">
                Este ticket foi marcado como resolvido. Se o problema persistir, você pode reabri-lo.
              </p>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default SupportTicketDetails;
